import { useEffect, useState, useCallback, useRef } from "react";
import { apiFetch } from "../context/AuthContext";

const B    = "#2C1A0E";
const ROSE = "#C9847A";
const fmtR = (n) => n ? `₹${Number(n).toLocaleString("en-IN")}` : "—";

export default function CustomersPage() {
  const [customers, setCustomers] = useState([]);
  const [total,     setTotal]     = useState(0);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState("");
  const [source,    setSource]    = useState("");
  const [segment,   setSegment]   = useState("");
  const [page,      setPage]      = useState(1);
  const [expanded,  setExpanded]  = useState(null);
  const searchTimer = useRef(null);
  const LIMIT = 50;

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    const params = new URLSearchParams({ page: p, limit: LIMIT });
    if (search)  params.set("search",  search);
    if (source)  params.set("source",  source);
    if (segment) params.set("segment", segment);
    const r = await apiFetch(`/api/customers?${params}`);
    if (r.ok) {
      const d = await r.json();
      setCustomers(d.customers || []);
      setTotal(d.total || 0);
    }
    setLoading(false);
  }, [search, source, segment]);

  useEffect(() => { setPage(1); load(1); }, [source, segment]);
  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setPage(1); load(1); }, 350);
  }, [search]);
  useEffect(() => { load(page); }, [page]);

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div style={{ padding: "28px 28px 80px", fontFamily: "Inter, sans-serif" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <div>
          <h1 style={{ fontFamily: "'Cormorant Garamond',Georgia,serif",
                       color: B, fontSize: 26, fontWeight: 700, margin: 0 }}>
            👥 Customers
          </h1>
          <p style={{ color: "#888", fontSize: 13, marginTop: 4 }}>
            Online (Shopify) + Offline (Zoho) · {total.toLocaleString()} total
          </p>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search name, email, phone, city…"
          style={{ ...inp, flex: 1, minWidth: 200 }}
        />
        <select value={source} onChange={e => setSource(e.target.value)} style={sel}>
          <option value="">All Sources</option>
          <option value="Shopify">Shopify (Online)</option>
          <option value="Zoho">Zoho (Offline)</option>
        </select>
        <select value={segment} onChange={e => setSegment(e.target.value)} style={sel}>
          <option value="">All Segments</option>
          <option value="B2C">B2C</option>
          <option value="B2B">B2B</option>
        </select>
      </div>

      {/* Table */}
      <div style={{ background: "#fff", border: "1px solid #EDE0D4", borderRadius: 12, overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "#F7F5F2" }}>
              <th style={th}>Name</th>
              <th style={th}>Email</th>
              <th style={th}>Phone</th>
              <th style={th}>City</th>
              <th style={th}>Source</th>
              <th style={th}>Segment</th>
              <th style={{ ...th, textAlign: "right" }}>Orders</th>
              <th style={{ ...th, textAlign: "right" }}>Total Spend</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} style={{ padding: 24, color: "#aaa", textAlign: "center" }}>Loading…</td></tr>
            )}
            {!loading && customers.length === 0 && (
              <tr><td colSpan={8} style={{ padding: 24, color: "#aaa", textAlign: "center" }}>
                No customers found{search ? ` for "${search}"` : ""}
              </td></tr>
            )}
            {!loading && customers.map(c => (
              <>
                <tr key={c._id}
                  onClick={() => setExpanded(expanded === c._id ? null : c._id)}
                  style={{ borderBottom: "1px solid #F0E8E0", cursor: "pointer",
                           background: expanded === c._id ? "#FDF8F5" : "#fff" }}>
                  <td style={{ ...td, fontWeight: 600, color: B }}>
                    <span style={{ marginRight: 6, fontSize: 10, color: "#aaa" }}>
                      {expanded === c._id ? "▼" : "▶"}
                    </span>
                    {c.name || "—"}
                  </td>
                  <td style={{ ...td, color: "#555" }}>{c.email || "—"}</td>
                  <td style={td}>{c.phone || "—"}</td>
                  <td style={td}>{c.city || "—"}</td>
                  <td style={td}>
                    <span style={{
                      background: c.source === "Shopify" ? "#DBEAFE" : "#F0FDF4",
                      color: c.source === "Shopify" ? "#1D4ED8" : "#15803D",
                      borderRadius: 4, padding: "2px 8px", fontSize: 11, fontWeight: 600,
                    }}>{c.source || "Manual"}</span>
                  </td>
                  <td style={td}>
                    <span style={{
                      background: c.segment === "B2B" ? "#FEF9C3" : "#FAF0F0",
                      color: c.segment === "B2B" ? "#854D0E" : ROSE,
                      borderRadius: 4, padding: "2px 8px", fontSize: 11, fontWeight: 600,
                    }}>{c.segment || "B2C"}</span>
                  </td>
                  <td style={{ ...td, textAlign: "right" }}>{c.total_orders || 0}</td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 600, color: B }}>
                    {fmtR(c.total_spend)}
                  </td>
                </tr>
                {expanded === c._id && (
                  <tr key={`${c._id}-detail`}>
                    <td colSpan={8} style={{ padding: "12px 24px 16px", background: "#FDF8F5",
                                             borderBottom: "1px solid #F0E8E0" }}>
                      <div style={{ display: "flex", gap: 24, flexWrap: "wrap", fontSize: 12 }}>
                        {c.tags && <span><strong>Tags:</strong> {c.tags}</span>}
                        {c.zoho_contact_id && <span><strong>Zoho ID:</strong> {c.zoho_contact_id}</span>}
                        {c.shopify_customer_id && <span><strong>Shopify ID:</strong> {c.shopify_customer_id}</span>}
                        {c.notes && <span><strong>Notes:</strong> {c.notes}</span>}
                      </div>
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 16 }}>
          <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1} style={pgBtn}>
            ← Prev
          </button>
          <span style={{ padding: "6px 12px", fontSize: 13, color: "#888" }}>
            {page} / {totalPages} ({total} total)
          </span>
          <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page === totalPages} style={pgBtn}>
            Next →
          </button>
        </div>
      )}
    </div>
  );
}

const inp = { border:"1px solid #DDD4C8", borderRadius:7, padding:"7px 12px",
              fontSize:13, background:"#FAF7F2", color:"#2C1A0E" };
const sel = { ...inp, minWidth: 140 };
const th  = { padding:"9px 14px", textAlign:"left", fontWeight:600, color:"#666",
              fontSize:11, textTransform:"uppercase", letterSpacing:0.5 };
const td  = { padding:"10px 14px", verticalAlign:"middle" };
const pgBtn = { background:"#F0EBE3", color:"#2C1A0E", border:"1px solid #DDD4C8",
                borderRadius:7, padding:"6px 14px", fontSize:12, cursor:"pointer" };
